import React from 'react'
import { Form, Input } from 'antd'
import PropTypes from 'prop-types'

// Общие кусочки для локализованных полей (KZ / RU / EN).
// Английский всегда необязателен: если поле пустое, приложение покажет русский
// (fallback en → ru → kz), поэтому старые записи без EN не ломаются.

export const LANGUAGE_LABELS = {
  KZ: 'каз',
  RU: 'рус',
  EN: 'англ'
}

/**
 * Необязательное поле на английском для antd-формы.
 *   <EnFormItem name="nameEN" label="Название(англ)" initialValue={nameEN} />
 * multiline — TextArea вместо Input; children — свой инпут (если нужен особый).
 */
export const EnFormItem = ({
  name,
  label,
  initialValue,
  multiline,
  rows,
  placeholder,
  children
}) => (
  <Form.Item
    key={name}
    label={label}
    name={name}
    initialValue={initialValue}
    extra="Необязательно: без перевода в приложении будет русский текст"
  >
    {children ||
      (multiline ? (
        <Input.TextArea rows={rows} placeholder={placeholder || 'English'} />
      ) : (
        <Input placeholder={placeholder || 'English'} />
      ))}
  </Form.Item>
)

EnFormItem.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  initialValue: PropTypes.any,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  placeholder: PropTypes.string,
  children: PropTypes.node
}

EnFormItem.defaultProps = {
  label: 'English',
  initialValue: undefined,
  multiline: false,
  rows: 4,
  placeholder: undefined,
  children: null
}

/**
 * Набор полей на трёх языках одним компонентом:
 *   <LocalizedFields base="name" label="Название" values={item} required={['KZ', 'RU']} />
 * suffixes — стиль суффиксов модели: ['KZ','RU','EN'] (по умолчанию) или ['Kaz','Rus','Eng'].
 */
export const LocalizedFields = ({
  base,
  label,
  values,
  required,
  multiline,
  suffixes
}) => {
  const langs = ['KZ', 'RU', 'EN']
  return (
    <>
      {langs.map((lang, i) => {
        const name = `${base}${suffixes[i]}`
        const fieldLabel = `${label}(${LANGUAGE_LABELS[lang]})`
        if (lang === 'EN') {
          return (
            <EnFormItem
              key={name}
              name={name}
              label={fieldLabel}
              initialValue={values?.[name]}
              multiline={multiline}
            />
          )
        }
        return (
          <Form.Item
            key={name}
            label={fieldLabel}
            name={name}
            initialValue={values?.[name]}
            rules={
              required.includes(lang)
                ? [
                    {
                      required: true,
                      message: `Пожалуйста, заполните ${fieldLabel}`
                    }
                  ]
                : []
            }
          >
            {multiline ? <Input.TextArea rows={4} /> : <Input />}
          </Form.Item>
        )
      })}
    </>
  )
}

LocalizedFields.propTypes = {
  base: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  values: PropTypes.objectOf(PropTypes.any),
  required: PropTypes.arrayOf(PropTypes.string),
  multiline: PropTypes.bool,
  suffixes: PropTypes.arrayOf(PropTypes.string)
}

LocalizedFields.defaultProps = {
  values: {},
  required: [],
  multiline: false,
  suffixes: ['KZ', 'RU', 'EN']
}

/** Колонка таблицы для EN-поля. */
export const enColumn = (dataIndex, title, extra = {}) => ({
  title,
  dataIndex,
  render: value => value || <span style={{ color: '#bbb' }}>—</span>,
  ...extra
})

/** Поле файла для EN-версии (картинка/аудио) с подписью. */
export const EnFileInput = ({
  label,
  accept,
  fileRef,
  onChange,
  currentUrl
}) => (
  <div style={{ marginBottom: '3%' }}>
    <div>{label}</div>
    <input
      type="file"
      accept={accept}
      ref={fileRef}
      onChange={event => onChange(event.target.files[0])}
    />
    {currentUrl ? (
      <div style={{ fontSize: 12, color: '#888' }}>
        Сейчас:{' '}
        <a href={currentUrl} target="_blank" rel="noopener noreferrer">
          {currentUrl.split('/').pop()}
        </a>
      </div>
    ) : (
      <div style={{ fontSize: 12, color: '#888' }}>
        Необязательно: без файла будет использована русская версия
      </div>
    )}
  </div>
)

EnFileInput.propTypes = {
  label: PropTypes.node,
  accept: PropTypes.string,
  fileRef: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  currentUrl: PropTypes.string
}

EnFileInput.defaultProps = {
  label: 'Файл (англ)',
  accept: 'image/*',
  fileRef: undefined,
  currentUrl: undefined
}
